import { ChangeDetectorRef, Component, Input, inject, AfterViewInit, OnDestroy } from '@angular/core';

import moment from 'moment';
import { PaymentService } from '../../services/payment.service';
import { HexcomProductSubscription } from '../../models/server/Payment/HexcomProductSubscription';
import { HexcomCustomer } from '../../models/server/Payment/HexcomCustomer';
import { Organisation } from '../../models/server/Organisation';
import { User } from '../../models/server/User';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '../../services/organisation.service';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MaterialModule } from '../../material.module';

@Component({
    selector: 'app-plan-expiry',
    templateUrl: './plan-expiry.component.html',
    imports: [MaterialModule, RouterLink, DatePipe]
})
export class PlanExpiryComponent implements AfterViewInit, OnDestroy {

  status: string;
  currentSub: HexcomProductSubscription;
  customer: HexcomCustomer;
  showStatus: boolean;
  trialEnd: Date;
  statusMessage: string;

  @Input()
  organisation: Organisation;
  @Input()
  user: User;

  private paymentSub: Subscription;
  private orgSub: Subscription;

  private snackBar = inject(MatSnackBar);
  private paymentService = inject(PaymentService);
  private organisationService = inject(OrganisationService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {

    this.orgSub = this.organisationService.orgUpdate.subscribe(o => {
      this.organisation = o;
      this.updateView();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.orgSub.unsubscribe();
    this.paymentSub.unsubscribe();
  }

  ngAfterViewInit() {
    this.paymentService.onCustomerFetch
      .subscribe((c: HexcomCustomer) => {
        this.currentSub = c.currentSubscription();
        this.updateView();
        this.cdr.markForCheck();
      });

    // force the first call
    this.paymentSub = this.paymentService.getCustomer().subscribe();
  }

  updateView() {
    if (this.organisation?.isDeactivated) {
      this.status = 'DEACTIVATED';
      this.statusMessage = this.organisation.deactivateReason;
    } else if (this.currentSub?.pauseFrom && !this.currentSub.cancelAtPeriodEnd) {
      // paused by the subscriber (#1883); the pause starts at the end of the period already paid for
      this.status = new Date(this.currentSub.pauseFrom).getTime() <= Date.now() ? 'PAUSED' : 'PAUSING';
    } else if (this.currentSub?.cancelAtPeriodEnd && ['active', 'trialing', 'past_due'].includes(this.currentSub.status)) {
      // cancelled, but still running until the end of the billing period
      this.status = 'ENDING';
    } else if (this.currentSub) {
      switch (this.currentSub.status) {
        case 'active':
          { this.status = 'OK'; break; }
        case 'trialing':
          { this.status = 'SUBTRIALING'; break; }
        case 'past_due':
          { this.status = 'OVERDUE'; break; }
        case 'unpaid':
          { this.status = 'EXPIRED'; break; }
        case 'canceled': // Stripe's spelling
        case 'cancelled':
          { this.status = 'CANCELLED'; break; }
      }
    } else if (this.organisation.trialEnds) {

      if (this.organisation.isTrialExpired) {
        this.status = 'TRIALENDED';
      }
      else {
        this.status = 'TRIALING';
      }
    } else if (this.organisation?.billByInvoice) {
      // Invoice-billed organisations are billed manually outside Stripe, so there is no
      // subscription record to report here - there's nothing meaningful to show.
      this.status = undefined;
    } else {
      this.status = 'NONE';
    }

    if (this.user && this.organisation) {
      this.showStatus = !!this.status && (this.user?.isSubscriber || this.status === 'FREE' || this.status === 'DEACTIVATED');
    }
  }

  showDeactivation(): void {
    if (this.organisation.isDeactivated) {
      this.snackBar.open(
        `Your organisation was deactivated ${moment(this.organisation.deactivateDate).format('ll')}: ${this.organisation.deactivateReason}`, 'Organisation Deactivated');
    }
  }
}
